/**
 * Super-admin — Toggle which menus are blocked per tenant.
 *
 *   GET   /super-admin/tenants/{id}/menus → {available_menus, disabled_menus}
 *   PATCH /super-admin/tenants/{id}/menus  body: {disabled_menus: []}
 *
 * UI: lista as empresas; ao selecionar uma, mostra todos os menus disponíveis
 * com toggles. Os marcados como "bloqueado" não aparecem na sidebar nem nas
 * rotas para os usuários daquele tenant (somente admin/colab; super-admin
 * sempre vê tudo).
 */
import React, { useEffect, useMemo, useState } from "react";
import {
  ToggleLeft,
  ToggleRight,
  Save,
  Search,
  RefreshCw,
  CheckCircle2,
  XCircle,
  Building2,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";

const MENU_LABELS = {
  dashboard: "Dashboard",
  today: "Resumo do dia",
  schedule: "Agenda",
  "schedule-release": "Liberação da agenda",
  clients: "Clientes",
  collaborators: "Colaboradores",
  teams: "Equipes",
  positions: "Cargos",
  supplies: "Itens de limpeza",
  equipments: "Equipamentos",
  expenses: "Kits de compra",
  estimates: "Invoice / Orçamentos",
  payments: "Histórico de pagamentos",
  receivables: "Recebimentos",
  invoices: "A Receber (Faturas)",
  finance: "Financeiro",
  "company-settings": "Dados da Empresa",
  "audit-log": "Audit Log",
  billing: "Plano & Cobrança",
  "status-colors": "Cores",
  "booking-requests": "Solicitações",
  notes: "Minhas notas",
  reports: "Relatórios gerais",
  history: "Histórico",
  notifications: "Notificações",
  "notification-settings": "Templates de mensagem",
  "notification-history": "Histórico de envios",
};

export default function SuperAdminMenus() {
  const [tenants, setTenants] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [data, setData] = useState(null);
  const [disabled, setDisabled] = useState([]);
  const [saving, setSaving] = useState(false);
  const [q, setQ] = useState("");

  async function loadTenants() {
    try {
      const { data } = await api.get("/super-admin/tenants");
      setTenants(data || []);
      if (!selectedId && data?.length) setSelectedId(data[0].id);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Falha");
    }
  }

  async function loadMenus(id) {
    try {
      const { data } = await api.get(`/super-admin/tenants/${id}/menus`);
      setData(data);
      setDisabled(data.disabled_menus || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Falha");
    }
  }

  useEffect(() => {
    loadTenants();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (selectedId) loadMenus(selectedId);
  }, [selectedId]);

  const filteredTenants = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return tenants;
    return tenants.filter((t) => (t.name || "").toLowerCase().includes(term));
  }, [tenants, q]);

  function toggle(menuKey) {
    setDisabled((arr) =>
      arr.includes(menuKey) ? arr.filter((k) => k !== menuKey) : [...arr, menuKey],
    );
  }

  async function save() {
    setSaving(true);
    try {
      const { data: fresh } = await api.patch(
        `/super-admin/tenants/${selectedId}/menus`,
        { disabled_menus: disabled },
      );
      toast.success(
        disabled.length === 0
          ? "Todos os menus liberados"
          : `${disabled.length} menu(s) bloqueado(s)`,
      );
      // Use the response payload as the new source-of-truth so the dirty
      // state instantly becomes `false` and the badge in the left list
      // also reflects the new count.
      setData(fresh);
      setDisabled(fresh.disabled_menus || []);
      await loadTenants();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Falha ao salvar");
    } finally {
      setSaving(false);
    }
  }

  const dirty =
    data &&
    JSON.stringify([...disabled].sort()) !==
      JSON.stringify([...(data.disabled_menus || [])].sort());

  return (
    <div className="space-y-5" data-testid="super-admin-menus-page">
      <header>
        <h1 className="text-2xl sm:text-3xl font-bold text-white">
          Menus por empresa
        </h1>
        <p className="text-sm text-slate-400 mt-1">
          Bloqueie ou libere módulos para cada tenant. Usuários da empresa não
          enxergam menus bloqueados e não conseguem acessar pela URL direta.
        </p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-[320px,1fr] gap-4">
        {/* Tenant list */}
        <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
          <div className="p-3 border-b border-white/10">
            <div className="relative">
              <Search
                size={14}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500"
              />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Buscar empresa…"
                data-testid="menus-tenant-search"
                className="w-full pl-9 pr-3 py-2 rounded-md border border-white/10 bg-slate-950 text-sm text-white placeholder:text-slate-500"
              />
            </div>
          </div>
          <div className="max-h-[60vh] overflow-y-auto">
            {filteredTenants.map((t) => {
              const active = t.id === selectedId;
              const blockedCount = (t.disabled_menus || []).length;
              return (
                <button
                  key={t.id}
                  onClick={() => setSelectedId(t.id)}
                  data-testid={`menus-tenant-${t.id}`}
                  className={`w-full flex items-center gap-2 px-3 py-2.5 text-left transition border-b border-white/5 last:border-0 ${
                    active
                      ? "bg-violet-600/30 text-white"
                      : "hover:bg-white/5 text-slate-200"
                  }`}
                >
                  <Building2 size={14} className="flex-shrink-0 text-violet-300" />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold truncate">{t.name}</div>
                    <div className="text-[11px] text-slate-400 truncate">
                      {blockedCount > 0
                        ? `${blockedCount} menu(s) bloqueado(s)`
                        : "todos os menus ativos"}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Menus toggles */}
        <div className="bg-white/5 border border-white/10 rounded-xl p-5">
          {!data ? (
            <div className="p-10 text-center text-slate-400">
              {selectedId ? "Carregando…" : "Selecione uma empresa"}
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
                <div>
                  <div className="text-xs uppercase tracking-wider text-violet-300 font-semibold">
                    Empresa
                  </div>
                  <h3 className="text-xl font-bold text-white">{data.tenant_name}</h3>
                </div>
                <div className="flex gap-2 items-center">
                  {dirty && (
                    <span
                      data-testid="menus-dirty-badge"
                      className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider px-2 py-1 rounded-full bg-amber-500/20 text-amber-200 border border-amber-500/30 animate-pulse"
                    >
                      Alterações não salvas
                    </span>
                  )}
                  <button
                    onClick={() => {
                      setDisabled(data.disabled_menus || []);
                      loadMenus(selectedId);
                    }}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-white/10 bg-white/5 hover:bg-white/10 text-sm font-semibold text-slate-200"
                  >
                    <RefreshCw size={13} /> Recarregar
                  </button>
                  <button
                    onClick={save}
                    disabled={!dirty || saving}
                    data-testid="menus-save"
                    className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-bold transition-all ${
                      dirty
                        ? "bg-violet-500 hover:bg-violet-400 text-white shadow-lg shadow-violet-500/50 ring-2 ring-violet-300/30"
                        : "bg-white/5 text-slate-500 cursor-not-allowed"
                    }`}
                  >
                    <Save size={14} /> {saving ? "Salvando…" : "Salvar"}
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {data.available_menus.map((m) => {
                  const isDisabled = disabled.includes(m);
                  return (
                    <button
                      key={m}
                      onClick={() => toggle(m)}
                      data-testid={`menu-toggle-${m}`}
                      className={`flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg border transition text-left ${
                        isDisabled
                          ? "bg-red-500/10 border-red-500/30 text-red-200"
                          : "bg-emerald-500/10 border-emerald-500/30 text-emerald-100"
                      }`}
                    >
                      <span className="flex items-center gap-2 min-w-0">
                        {isDisabled ? (
                          <XCircle size={14} className="flex-shrink-0" />
                        ) : (
                          <CheckCircle2 size={14} className="flex-shrink-0" />
                        )}
                        <span className="text-sm font-semibold truncate">
                          {MENU_LABELS[m] || m}
                        </span>
                      </span>
                      {isDisabled ? (
                        <ToggleLeft size={20} className="text-red-300 flex-shrink-0" />
                      ) : (
                        <ToggleRight size={20} className="text-emerald-300 flex-shrink-0" />
                      )}
                    </button>
                  );
                })}
              </div>

              <p className="text-xs text-slate-400 mt-4">
                Verde = liberado · Vermelho = bloqueado. As mudanças entram em
                vigor no próximo login do usuário (ou recarregamento da página).
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
